import React, { useCallback, useEffect } from 'react';
import { AppState, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useRouter } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import { VideoView, useVideoPlayer } from 'expo-video';
import { useIsFocused } from 'expo-router';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import { PaperFeedButton } from '../ui/Button';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { useOptionalResolvedMessage, useResolvedMessage } from '../../lib/i18n/messages';

export type ProjectOnboardingCardState = 'preparing' | 'ready' | 'no-results';
export type ProjectOnboardingCardVariant = 'default' | 'billing-preview' | 'forwarded';

interface ProjectOnboardingCardProps {
    state: ProjectOnboardingCardState;
    feedHref: string;
    editHref: string;
    variant?: ProjectOnboardingCardVariant;
}

const DEFAULT_CARD_CONTENT: Record<ProjectOnboardingCardState, {
    eyebrow: MessageDescriptor;
    title: MessageDescriptor;
    message: MessageDescriptor;
    icon: React.ComponentProps<typeof FontAwesome6>['name'];
    primaryAction?: {
        label: MessageDescriptor;
        href: 'feed' | 'edit';
        testID: string;
    };
}> = {
    preparing: {
        eyebrow: msg({ id: 'projectOnboarding.preparing.eyebrow', message: 'Preparing your project' }),
        title: msg({ id: 'projectOnboarding.preparing.title', message: 'We are building your first set of results.' }),
        message: msg({ id: 'projectOnboarding.preparing.message', message: 'We are matching your project against new papers now. You can leave this page and come back later. Digest emails will still go out when something is ready.' }),
        icon: 'magnifying-glass',
    },
    ready: {
        eyebrow: msg({ id: 'projectOnboarding.ready.eyebrow', message: 'First results ready' }),
        title: msg({ id: 'projectOnboarding.ready.title', message: 'Your first papers are in.' }),
        message: msg({ id: 'projectOnboarding.ready.message', message: 'We found matching papers and added them to your feed. Open it to review what landed first.' }),
        icon: 'circle-check',
        primaryAction: {
            label: msg({ id: 'handoff.paperFeed', message: 'Paper Feed' }),
            href: 'feed',
            testID: 'project-onboarding-open-feed',
        },
    },
    'no-results': {
        eyebrow: msg({ id: 'projectOnboarding.empty.eyebrow', message: 'First pass finished' }),
        title: msg({ id: 'projectOnboarding.empty.title', message: 'Nothing matched yet.' }),
        message: msg({ id: 'projectOnboarding.empty.message', message: 'The first pass completed, but no papers were a fit. We will keep checking new papers automatically. If you want broader coverage, adjust the project criteria.' }),
        icon: 'compass',
        primaryAction: {
            label: msg({ id: 'projectOnboarding.editProject', message: 'Edit Project' }),
            href: 'edit',
            testID: 'project-onboarding-edit-project',
        },
    },
};

const BILLING_PREVIEW_CARD_CONTENT: typeof DEFAULT_CARD_CONTENT = {
    preparing: {
        eyebrow: msg({ id: 'projectOnboarding.preview.preparingEyebrow', message: 'Preparing your one-time preview' }),
        title: msg({ id: 'projectOnboarding.preview.preparingTitle', message: 'We are finding your first matching papers.' }),
        message: msg({ id: 'projectOnboarding.preview.preparingMessage', message: 'We are running a one-time search of recent research for your project. No action is needed. You can leave this page, and we will email up to three of the strongest matches when your preview is ready.' }),
        icon: 'magnifying-glass',
    },
    ready: {
        eyebrow: msg({ id: 'projectOnboarding.preview.readyEyebrow', message: 'First preview ready' }),
        title: msg({ id: 'projectOnboarding.preview.readyTitle', message: 'Your first matching papers are ready.' }),
        message: msg({ id: 'projectOnboarding.preview.readyMessage', message: 'We found matching papers in this one-time search. Tracking pauses when the preview is complete unless you start a free trial.' }),
        icon: 'circle-check',
    },
    'no-results': {
        eyebrow: msg({ id: 'projectOnboarding.preview.emptyEyebrow', message: 'Finishing your one-time preview' }),
        title: msg({ id: 'projectOnboarding.preview.emptyTitle', message: 'No matching paper yet.' }),
        message: msg({ id: 'projectOnboarding.preview.emptyMessage', message: 'We are finishing this one-time search. Tracking pauses when the preview ends unless you start a free trial.' }),
        icon: 'compass',
    },
};

const FORWARDED_CARD_CONTENT: Record<ProjectOnboardingCardState, {
    eyebrow: MessageDescriptor;
    title: MessageDescriptor;
    message: MessageDescriptor;
    icon: React.ComponentProps<typeof FontAwesome6>['name'];
    primaryAction?: {
        label: MessageDescriptor;
        href: 'feed' | 'edit';
        testID: string;
    };
}> = {
    preparing: {
        eyebrow: msg({ id: 'projectOnboarding.forwarded.preparingEyebrow', message: 'Topic live' }),
        title: msg({ id: 'projectOnboarding.forwarded.preparingTitle', message: 'We are organizing your imported papers.' }),
        message: msg({ id: 'projectOnboarding.forwarded.preparingMessage', message: 'Your topic is active now. Paperzilla is adding papers from your forwarded alerts to it as they resolve, and it is still processing newly arriving papers in the background. The original forwarded emails remain visible in Inbox.' }),
        icon: 'magnifying-glass',
    },
    ready: {
        eyebrow: msg({ id: 'projectOnboarding.forwarded.readyEyebrow', message: 'First matches ready' }),
        title: msg({ id: 'projectOnboarding.forwarded.readyTitle', message: 'Imported papers were added to your topic.' }),
        message: msg({ id: 'projectOnboarding.forwarded.readyMessage', message: 'Open your feed to review the first results. The original forwarded emails remain visible in Inbox.' }),
        icon: 'circle-check',
        primaryAction: {
            label: msg({ id: 'handoff.paperFeed', message: 'Paper Feed' }),
            href: 'feed',
            testID: 'project-onboarding-open-feed',
        },
    },
    'no-results': {
        eyebrow: msg({ id: 'projectOnboarding.forwarded.emptyEyebrow', message: 'Still watching' }),
        title: msg({ id: 'projectOnboarding.forwarded.emptyTitle', message: 'Nothing is ready in your feed yet.' }),
        message: msg({ id: 'projectOnboarding.forwarded.emptyMessage', message: 'Paperzilla has not surfaced any feed results yet. The original forwarded emails remain visible in Inbox, and it will keep checking automatically.' }),
        icon: 'compass',
        primaryAction: {
            label: msg({ id: 'projectOnboarding.editProject', message: 'Edit Project' }),
            href: 'edit',
            testID: 'project-onboarding-edit-project',
        },
    },
};

export default function ProjectOnboardingCard({
    state,
    feedHref,
    editHref,
    variant = 'default',
}: ProjectOnboardingCardProps) {
    const router = useRouter();
    const { fontsLoaded } = useFontLoading();
    const isFocused = useIsFocused();
    const shouldRenderPreparingVideo = state === 'preparing' && isFocused;
    const player = useVideoPlayer(require('../../assets/paperzilla-crunching.mp4'), (videoPlayer) => {
        videoPlayer.loop = true;
        videoPlayer.muted = true;
    });
    const content = (
        variant === 'forwarded'
            ? FORWARDED_CARD_CONTENT
            : variant === 'billing-preview'
                ? BILLING_PREVIEW_CARD_CONTENT
                : DEFAULT_CARD_CONTENT
    )[state];
    const eyebrow = useResolvedMessage(content.eyebrow);
    const title = useResolvedMessage(content.title);
    const message = useResolvedMessage(content.message);
    const primaryActionLabel = useOptionalResolvedMessage(content.primaryAction?.label);

    const playIfPossible = useCallback(() => {
        if (!shouldRenderPreparingVideo) {
            return;
        }

        try {
            const maybePromise = player.play() as unknown;
            if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
                (maybePromise as Promise<void>).catch((error: any) => {
                    if (error?.name !== 'AbortError') {
                        console.warn('[ProjectOnboardingCard] Failed to start preparing animation:', error);
                    }
                });
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.warn('[ProjectOnboardingCard] Failed to start preparing animation:', error);
            }
        }
    }, [player, shouldRenderPreparingVideo]);

    useEffect(() => {
        if (!shouldRenderPreparingVideo) {
            player.pause();
            return;
        }

        playIfPossible();

        return () => {
            player.pause();
        };
    }, [player, playIfPossible, shouldRenderPreparingVideo]);

    useEffect(() => {
        if (!shouldRenderPreparingVideo) {
            return;
        }

        const resumePlayback = () => {
            playIfPossible();
        };

        if (Platform.OS === 'web' && typeof document !== 'undefined' && typeof window !== 'undefined') {
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    resumePlayback();
                }
            };

            window.addEventListener('focus', resumePlayback);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                window.removeEventListener('focus', resumePlayback);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }

        const subscription = AppState.addEventListener('change', (nextState) => {
            if (nextState === 'active') {
                resumePlayback();
            }
        });

        return () => {
            subscription.remove();
        };
    }, [playIfPossible, shouldRenderPreparingVideo]);

    const primaryHref = content.primaryAction?.href === 'edit'
        ? editHref
        : feedHref;
    const primaryButtonStyle = [
        styles.primaryButton,
        state === 'no-results' ? styles.primaryButtonWarm : undefined
    ];

    return (
        <View
            style={[
                styles.card,
                state === 'ready' && styles.cardReady,
                state === 'no-results' && styles.cardNoResults,
            ]}
            testID={`project-onboarding-card-${state}`}
        >
            <View style={styles.headerRow}>
                <View style={[
                    styles.iconBadge,
                    state === 'ready' && styles.iconBadgeReady,
                    state === 'no-results' && styles.iconBadgeNoResults,
                ]}
                >
                    <FontAwesome6
                        name={content.icon}
                        size={16}
                        color={state === 'ready' ? colors.primaryGreenDark : state === 'no-results' ? '#92400e' : '#ffffff'}
                    />
                </View>
                <Text style={[styles.eyebrow, fontsLoaded && interSemiBold]}>
                    {eyebrow}
                </Text>
            </View>

            <Text style={[styles.title, fontsLoaded && interSemiBold]}>
                {title}
            </Text>
            <Text style={[styles.message, fontsLoaded && interRegular]}>
                {message}
            </Text>

            {state === 'preparing' && variant === 'billing-preview' ? (
                <View style={styles.previewPauseNotice} testID="project-onboarding-preview-pause-notice">
                    <View style={styles.previewPauseIcon}>
                        <FontAwesome6 name="pause" size={11} color={colors.primaryGreenDark} />
                    </View>
                    <Text style={[styles.previewPauseText, fontsLoaded && interRegular]}><Trans id="digest.projectonboardingcard.afterThisPreviewYourProjectWillPauseStartAFree" comment="UI copy in components/digest/ProjectOnboardingCard.tsx">
                        After this preview, your project will pause. Start a free trial to keep checking for new papers and receive future digest emails.
                    </Trans></Text>
                </View>
            ) : null}

            {state === 'preparing' && (
                <View style={styles.preparingVideoShell} testID="project-onboarding-preparing-video">
                    <VideoView
                        style={styles.preparingVideo}
                        player={player}
                        fullscreenOptions={{ enable: false }}
                        allowsPictureInPicture={false}
                        nativeControls={false}
                        playsInline
                        contentFit="contain"
                    />
                </View>
            )}

            {content.primaryAction && (
                <View style={styles.actionRow}>
                    {content.primaryAction.href === 'feed' ? (
                        <PaperFeedButton
                            style={primaryButtonStyle}
                            activeOpacity={0.8}
                            onPress={() => router.push(primaryHref)}
                            testID={content.primaryAction.testID}
                        />
                    ) : (
                        <TouchableOpacity
                            style={primaryButtonStyle}
                            activeOpacity={0.8}
                            onPress={() => router.push(primaryHref)}
                            testID={content.primaryAction.testID}
                        >
                            <Text style={[styles.primaryButtonText, fontsLoaded && interSemiBold]}>
                                {primaryActionLabel}
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: '#f3f9ee',
        borderColor: '#dcebcf',
        borderRadius: 24,
        borderWidth: 1,
        marginBottom: 24,
        overflow: 'hidden',
        paddingHorizontal: 24,
        paddingVertical: 24,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#0b1020',
            shadowOpacity: 0.06,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 8 },
            elevation: 3,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 10px 30px rgba(11,16,32,0.06)',
        }),
    },
    cardReady: {
        backgroundColor: '#f0fdf7',
        borderColor: '#bbf7d0',
    },
    cardNoResults: {
        backgroundColor: '#fffaf0',
        borderColor: '#fcdba6',
    },
    previewPauseNotice: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 14,
        paddingHorizontal: 13,
        paddingVertical: 11,
        borderRadius: 12,
        backgroundColor: '#e5f0db',
        borderWidth: 1,
        borderColor: '#cfe2bf',
    },
    previewPauseIcon: {
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#d2e5c2',
        flexShrink: 0,
    },
    previewPauseText: {
        flex: 1,
        color: colors.primaryGreenDark,
        fontSize: 13,
        lineHeight: 19,
    },
    headerRow: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 12,
        marginBottom: 14,
    },
    iconBadge: {
        alignItems: 'center',
        backgroundColor: colors.primaryGreen,
        borderRadius: 999,
        height: 34,
        justifyContent: 'center',
        width: 34,
    },
    iconBadgeReady: {
        backgroundColor: '#dcfce7',
    },
    iconBadgeNoResults: {
        backgroundColor: '#fef3c7',
    },
    eyebrow: {
        ...fonts.label,
        color: colors.text700,
        letterSpacing: 0.48,
        textTransform: 'uppercase',
    },
    title: {
        ...fonts.h2,
        color: colors.text900,
        marginBottom: 12,
    },
    message: {
        ...fonts.bodyMedium,
        color: colors.text600,
        marginBottom: 18,
        maxWidth: 640,
    },
    preparingVideoShell: {
        backgroundColor: '#ffffff',
        borderColor: 'rgba(90, 143, 50, 0.12)',
        borderRadius: 20,
        borderWidth: 1,
        marginTop: 4,
        overflow: 'hidden',
        padding: 12,
    },
    preparingVideo: {
        borderRadius: 14,
        height: 280,
        width: '100%',
    },
    actionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 6,
    },
    primaryButton: {
        alignItems: 'center',
        backgroundColor: colors.primaryGreenDark,
        borderRadius: 999,
        justifyContent: 'center',
        minHeight: 46,
        minWidth: 172,
        paddingHorizontal: 22,
        paddingVertical: 12,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    primaryButtonWarm: {
        backgroundColor: '#92400e',
    },
    primaryButtonText: {
        ...fonts.button,
        color: '#ffffff',
    },
});
